import { StyledEngineProvider, Theme, ThemeProvider } from '@mui/material/styles';
import ModalProvider from 'mui-modal-provider';
import * as React from 'react';
import { Route, Switch } from 'react-router-dom';
import SplashScreen from './components/Shared/SplashScreen/SplashScreen';
import AuthRequestStatus from './enums/AuthRequestStatus';
import ActivateSiCPage from './pages/ActivateSiCPage/ActivateSiCPage';
import ActivityReportPage from './pages/ActivityReportPage';
import AiContactPlanItemEventPage from './pages/AiContactPlanItemEventPage';
import AiCreateEditPromptPage from './pages/AiCreateEditPromptPage/AiCreateEditPromptPage';
import AiPromptManagementPage from './pages/AiPromptManagementPage/AiPromptManagementPage';
import AiSettingsPage from './pages/AiSettingsPage/AiSettingsPage';
import AiSmartReplyFlowPage from './pages/AiSmartReplyFlowPage/AiSmartReplyFlowPage';
import AiSmartReplyPage from './pages/AiSmartReplyPage/AiSmartReplyPage';
import CalendarPage from './pages/CalendarPage/CalendarPage';
import CalendarSettingsPage from './pages/CalendarSettingsPage/CalendarSettingsPage';
import ChangePasswordPage from './pages/ChangePasswordPage/ChangePasswordPage';
import { ChangeSicNumberPage } from './pages/ChangeSicNumberPage';
import CommunicatorPage from './pages/CommunicatorPage/CommunicatorPage';
import ContactDisplayPage from './pages/ContactDisplayPage/ContactDisplayPage';
import ContactStatusSettings from './pages/ContactStatusSettingsPage/ContactStatusSettingsPage';
import ConversationGridPage from './pages/ConversationGridPage/ConversationGridPage';
import CustomFieldsPage from './pages/CustomFieldsPage/CustomFieldsPage';
import CustomViewsPage from './pages/CustomViewsPage/CustomViewsPage';
import GeneralSettingsPage from './pages/GeneralSettingsPage/GeneralSettingsPage';
import { GettingStartedPage } from './pages/GettingStartedPage';
import GoogleSettingsPage from './pages/GoogleSettingsPage/GoogleSettingsPage';
import HelpArticlePage from './pages/HelpPage/HelpArticlePage';
import HelpCategoryPage from './pages/HelpPage/HelpCategoryPage';
import HelpPage from './pages/HelpPage/HelpPage';
import ImportExportPage from './pages/ImportExportPage/ImportExportPage';
import IntegrationsSettingsPage from './pages/IntegrationsSettingsPage/IntegrationsSettingsPage';
import LoginPage from './pages/LoginPage/LoginPage';
import MainPage from './pages/MainPage/MainPage';
import { NoSicPage } from './pages/NoSicPage';
import NotificationSettingsPage from './pages/NotificationSettingsPage/NotificationSettingsPage';
import OfferPage from './pages/OfferPage/OfferPage';
import OrderCancelledPage from './pages/OrderCancelledPage';
import OrderSuccessPage from './pages/OrderSuccessPage';
import { PhoneSettingsPage } from './pages/PhoneSettingsPage';
import { SupportPage } from './pages/SupportPage';
import UserEmailSettingsPage from './pages/UserEmailSettingsPage/UserEmailSettingsPage';
import UsersCreateEditPage from './pages/UsersCreateEditPage/UsersCreateEditPage';
import UsersPage from './pages/UsersPage/UsersPage';
import { WebPushPage } from './pages/WebPushPage';
import * as routes from './routes';
import ActivateSicRoute from './routes/ActivateSicRoute';
import NoSicRoute from './routes/NoSicRoute';
import ProtectedRoute from './routes/ProtectedRoute';
import UnauthorizedRoute from './routes/UnauthorizedRoute';
import { INBOX_FILTER_INITIAL_STATE } from './sic-lib/defaults';
import { userRoles } from './sic-lib/types/UserRoles';
import { useAppDispatch, useAppSelector } from './store/hooks';
import { setFilters } from './store/inbox';
import { requestConfig, requestVerifyAuth } from './store/users';
import { getTheme } from './theme';
import { AuthRoutesState } from './types/AuthRoutesState';

declare module '@mui/styles/defaultTheme' {
    // eslint-disable-next-line @typescript-eslint/no-empty-interface
    interface DefaultTheme extends Theme {}
}

export default function App() {
    const dispatch = useAppDispatch();

    const user = useAppSelector(state => state.user);

    const authRouteState: AuthRoutesState = {
        requestStatus: user.authStatus.requestStatus,
        isImpersonated: user.isImpersonated,
        clientId: user.clientId,
        hasReturningAuth: user.authStatus.hasReturningAuth,
        clientHasSic: user.clientHasSic,
        dialerIsEnabled: user.dialerIsEnabled,
        dialerIsSetup: user.dialerIsSetup,
        dialerIsSic: user.dialerIsSic,
        userRoles: user.userRoles,
    };

    React.useEffect(() => {
        /*
                TIP: This should only run the first time the page loads, should not have a chance to run 
                anytime the authRequestStatus changes.
             */
        dispatch(requestVerifyAuth());
    }, []);

    React.useEffect(() => {
        if (user.authStatus.requestStatus === AuthRequestStatus.authSuccess) {
            /* user has been successfuly authenticated; trigger request for user config */
            dispatch(requestConfig());
        } else if (user.authStatus.requestStatus === AuthRequestStatus.configSuccess) {
            /* 
                user has been successfully authenticated; request required data for app initialization 
                TODO: refactor into page-level requests, where able, when structure is better suited to make those decisions
            */
            dispatch(setFilters({ filter: user.inboxFilter || INBOX_FILTER_INITIAL_STATE }));
        }
    }, [user.authStatus.requestStatus]);

    const userTheme = React.useMemo(() => getTheme(user.businessLine), [user.businessLine]);

    /*
        show an app loading screen while:
        1. user has arrived on page and auth flow is yet to begin
        2. OR app is checking/verifying auth from previous "session"
        2. OR user has a valid auth from previous "session" AND app is requesting config
    */
    if (
        user.authStatus.requestStatus === AuthRequestStatus.none ||
        user.authStatus.requestStatus === AuthRequestStatus.verify ||
        user.authStatus.requestStatus === AuthRequestStatus.authSuccess ||
        (user.authStatus.hasReturningAuth &&
            user.authStatus.requestStatus === AuthRequestStatus.active)
    ) {
        return (
            <StyledEngineProvider injectFirst>
                <ThemeProvider theme={userTheme}>
                    <SplashScreen status={user.authStatus.requestStatus} />
                </ThemeProvider>
            </StyledEngineProvider>
        );
    }

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={userTheme}>
                <ModalProvider suspense={false}>
                    <Switch>
                        <Route exact path={routes.offersRoute}>
                            <OfferPage />
                        </Route>
                        <Route exact path={routes.orderRoutes.success}>
                            <OrderSuccessPage />
                        </Route>
                        <Route exact path={routes.orderRoutes.cancelled}>
                            <OrderCancelledPage />
                        </Route>
                        {/* <Route exact path={routes.checkoutRoute}>
                        <CheckoutPage />
                    </Route> */}
                        <NoSicRoute authRoutesState={authRouteState} exact path={routes.noSicRoute}>
                            <NoSicPage />
                        </NoSicRoute>
                        <ActivateSicRoute
                            authRoutesState={authRouteState}
                            exact
                            path={routes.migrateFromEngage}
                        >
                            <ActivateSiCPage />
                        </ActivateSicRoute>
                        <UnauthorizedRoute
                            authRoutesState={authRouteState}
                            exact
                            path={routes.loginRoute}
                        >
                            <LoginPage />
                        </UnauthorizedRoute>
                        <ProtectedRoute
                            authRoutesState={authRouteState}
                            exact
                            path={routes.supportRoute}
                            requiredRoles={userRoles.support}
                        >
                            <SupportPage />
                        </ProtectedRoute>
                        <ProtectedRoute
                            authRoutesState={authRouteState}
                            exact
                            path="/"
                            requiredRoles={userRoles.users}
                        >
                            <MainPage />
                        </ProtectedRoute>
                        <ProtectedRoute
                            authRoutesState={authRouteState}
                            path={routes.contactRoutes.details}
                            requiredRoles={userRoles.users}
                        >
                            <ContactDisplayPage />
                        </ProtectedRoute>
                        <ProtectedRoute
                            authRoutesState={authRouteState}
                            path={routes.contactRoutes.groups}
                            requiredRoles={userRoles.users}
                        >
                            <ConversationGridPage />
                        </ProtectedRoute>
                        <ProtectedRoute
                            authRoutesState={authRouteState}
                            path={routes.communicatorRoute}
                            requiredRoles={userRoles.users}
                        >
                            <CommunicatorPage />
                        </ProtectedRoute>
                        <ProtectedRoute
                            authRoutesState={authRouteState}
                            path={routes.settingsRoutes.customFields}
                            requiredRoles={userRoles.users}
                        >
                            <CustomFieldsPage />
                        </ProtectedRoute>
                        <ProtectedRoute
                            authRoutesState={authRouteState}
                            path={routes.settingsRoutes.customViews}
                            requiredRoles={userRoles.users}
                        >
                            <CustomViewsPage />
                        </ProtectedRoute>
                        <ProtectedRoute
                            authRoutesState={authRouteState}
                            path={routes.settingsRoutes.createEditUser}
                            requiredRoles={userRoles.users}
                        >
                            <UsersCreateEditPage />
                        </ProtectedRoute>
                        <ProtectedRoute
                            authRoutesState={authRouteState}
                            path={routes.settingsRoutes.users}
                            requiredRoles={userRoles.users}
                        >
                            <UsersPage />
                        </ProtectedRoute>
                        <ProtectedRoute
                            authRoutesState={authRouteState}
                            path={routes.settingsRoutes.changePassword}
                            requiredRoles={userRoles.users}
                        >
                            <ChangePasswordPage />
                        </ProtectedRoute>

                        <ProtectedRoute
                            authRoutesState={authRouteState}
                            path={routes.settingsRoutes.importExport}
                            requiredRoles={userRoles.users}
                        >
                            <ImportExportPage />
                        </ProtectedRoute>
                        <ProtectedRoute
                            authRoutesState={authRouteState}
                            path={routes.settingsRoutes.general}
                            requiredRoles={userRoles.users}
                        >
                            <GeneralSettingsPage />
                        </ProtectedRoute>
                        <ProtectedRoute
                            authRoutesState={authRouteState}
                            path={routes.settingsRoutes.notification}
                            requiredRoles={userRoles.users}
                        >
                            <NotificationSettingsPage />
                        </ProtectedRoute>
                        <ProtectedRoute
                            authRoutesState={authRouteState}
                            path={routes.settingsRoutes.userEmail}
                            requiredRoles={userRoles.users}
                        >
                            <UserEmailSettingsPage />
                        </ProtectedRoute>
                        <ProtectedRoute
                            authRoutesState={authRouteState}
                            path={routes.settingsRoutes.contactStatus}
                            requiredRoles={userRoles.users}
                        >
                            <ContactStatusSettings />
                        </ProtectedRoute>
                        <ProtectedRoute
                            authRoutesState={authRouteState}
                            path={routes.calendarRouteBase}
                            requiredRoles={userRoles.users}
                        >
                            <CalendarPage />
                        </ProtectedRoute>
                        <ProtectedRoute
                            authRoutesState={authRouteState}
                            path={routes.settingsRoutes.calendar}
                            requiredRoles={userRoles.users}
                        >
                            <CalendarSettingsPage />
                        </ProtectedRoute>
                        <ProtectedRoute
                            authRoutesState={authRouteState}
                            path={routes.settingsRoutes.google}
                            requiredRoles={userRoles.users}
                        >
                            <GoogleSettingsPage />
                        </ProtectedRoute>
                        <ProtectedRoute
                            authRoutesState={authRouteState}
                            path={routes.settingsRoutes.aiCreateEditPrompt}
                            requiredRoles={userRoles.techSupport}
                        >
                            <AiCreateEditPromptPage />
                        </ProtectedRoute>
                        <ProtectedRoute
                            authRoutesState={authRouteState}
                            path={routes.settingsRoutes.aiPrompts}
                            requiredRoles={userRoles.techSupport}
                        >
                            <AiPromptManagementPage />
                        </ProtectedRoute>
                        <ProtectedRoute
                            authRoutesState={authRouteState}
                            path={routes.settingsRoutes.aiBuildSmartReplyPlan}
                            requiredRoles={userRoles.techSupport}
                        >
                            <AiSmartReplyFlowPage />
                        </ProtectedRoute>
                        <ProtectedRoute
                            authRoutesState={authRouteState}
                            path={routes.settingsRoutes.aiSmartReply}
                            requiredRoles={userRoles.techSupport}
                        >
                            <AiSmartReplyPage />
                        </ProtectedRoute>
                        <ProtectedRoute
                            authRoutesState={authRouteState}
                            path={routes.settingsRoutes.aiContactPlanItemEvent}
                            requiredRoles={userRoles.techSupport}
                        >
                            <AiContactPlanItemEventPage />
                        </ProtectedRoute>
                        <ProtectedRoute
                            authRoutesState={authRouteState}
                            path={routes.settingsRoutes.ai}
                            requiredRoles={userRoles.users}
                        >
                            <AiSettingsPage />
                        </ProtectedRoute>
                        <ProtectedRoute
                            authRoutesState={authRouteState}
                            path={routes.settingsRoutes.integrations}
                            requiredRoles={userRoles.admins}
                        >
                            <IntegrationsSettingsPage />
                        </ProtectedRoute>
                        {/* <ProtectedRoute
                        authRoutesState={authRouteState}
                        path={routes.settingsRoutes.packageUsage}
                        requiredRoles={userRoles.admins}
                    >
                        <PackageUsagePage />
                    </ProtectedRoute> */}
                        <ProtectedRoute
                            authRoutesState={authRouteState}
                            path={routes.settingsRoutes.phoneSettings}
                            requiredRoles={userRoles.users}
                        >
                            <PhoneSettingsPage />
                        </ProtectedRoute>
                        <ProtectedRoute
                            authRoutesState={authRouteState}
                            path={routes.settingsRoutes.changeSicNumber}
                            requiredRoles={userRoles.users}
                        >
                            <ChangeSicNumberPage />
                        </ProtectedRoute>
                        <ProtectedRoute
                            authRoutesState={authRouteState}
                            path={routes.campaignRoutes.webPush}
                            requiredRoles={userRoles.users}
                        >
                            <WebPushPage />
                        </ProtectedRoute>
                        <ProtectedRoute
                            authRoutesState={authRouteState}
                            path={routes.gettingStartedRoute}
                            requiredRoles={userRoles.users}
                        >
                            <GettingStartedPage />
                        </ProtectedRoute>
                        <ProtectedRoute
                            authRoutesState={authRouteState}
                            path={routes.helpRoutes.category}
                            requiredRoles={userRoles.users}
                        >
                            <HelpCategoryPage />
                        </ProtectedRoute>
                        <ProtectedRoute
                            authRoutesState={authRouteState}
                            path={routes.helpRoutes.article}
                            requiredRoles={userRoles.users}
                        >
                            <HelpArticlePage />
                        </ProtectedRoute>
                        <ProtectedRoute
                            authRoutesState={authRouteState}
                            path={routes.helpRoutes.helpHome}
                            requiredRoles={userRoles.users}
                        >
                            <HelpPage />
                        </ProtectedRoute>
                        <ProtectedRoute
                            authRoutesState={authRouteState}
                            path={routes.activityReportRoute}
                            requiredRoles={userRoles.users}
                        >
                            <ActivityReportPage />
                        </ProtectedRoute>
                    </Switch>
                </ModalProvider>
            </ThemeProvider>
        </StyledEngineProvider>
    );
}
